import { DestroyRef, Service, inject } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Observable, combineLatest, defer, filter, distinctUntilChanged, switchMap, map, of, shareReplay, catchError, retry, take } from 'rxjs';

import { AbstractionLayerConfig, environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { LaunchdarklyService } from 'src/app/services/launchdarkly.service';
import { ApolloClientService } from 'src/app/services/apollo-client.service';

/**
 * Loads the hosted `analytics.sdk.js` and mirrors Apollo auth: same GraphQL host as
 * {@link AbstractionLayerConfig} and `Authorization: sessionId` via the SDK.
 */
@Service()
export class BrowserAnalyticsService {
	private authService = inject(AuthService);

	private launchdarklyService = inject(LaunchdarklyService);

	private apolloClientService = inject(ApolloClientService);

	private route = inject(ActivatedRoute);

	private sdkLoad$: Observable<void> | null = null;

	/** sessionId + graphqlUrl — re-init when LaunchDarkly flips staging after first init. */
	private lastInitKey: string | null = null;

	/**
	 * Call once from `AppComponent` after theme init. Subscribes for the app lifetime.
	 * @param router
	 * @param destroyRef
	 */
	public start(router: Router, destroyRef: DestroyRef): void {
		this.loadSdkOnce$()
			.pipe(
				retry({ count: 2, delay: 1500 }),
				catchError(() => of(undefined)),
				takeUntilDestroyed(destroyRef),
			)
			.subscribe();

		combineLatest({
			userData: this.authService.userData$,
			launchDarklyFlags: this.launchdarklyService.launchDarklyFlagsWithFallback$,
			authSettled: this.route.queryParamMap.pipe(
				map(params => !params.get(environment.oneTimeTokenName)),
				distinctUntilChanged(),
			),
		})
			.pipe(
				filter(({ userData, authSettled }) => authSettled && !!userData.sessionID && !!userData.isLoggedIn),
				distinctUntilChanged((a, b) => a.userData.sessionID === b.userData.sessionID && a.launchDarklyFlags['staging'] === b.launchDarklyFlags['staging']),
				switchMap(payload =>
					this.apolloClientService.apolloClient$.pipe(
						take(1),
						switchMap(() =>
							this.loadSdkOnce$().pipe(
								filter(() => !!window.AnalyticsSDK),
								map(() => payload),
							),
						),
					),
				),
				takeUntilDestroyed(destroyRef),
			)
			.subscribe(({ userData, launchDarklyFlags }) => {
				const sid = userData.sessionID as string;
				const useStaging = launchDarklyFlags['staging'] !== false;
				const graphqlUrl = useStaging ? AbstractionLayerConfig.stagingUri : AbstractionLayerConfig.releaseUri;
				const initKey = `${sid}|${graphqlUrl}`;

				if (this.lastInitKey === initKey) {
					return;
				}
				this.lastInitKey = initKey;

				window.AnalyticsSDK!.init({
					appId: environment.analyticsAppId,
					graphqlUrl,
					sessionId: sid,
					getUsername: () => localStorage.getItem('userName') ?? '',
					autoTrackApiRequests: true,
				});

				window.AnalyticsSDK!.track('page_view', {
					title: document.title,
				});
			});

		router.events
			.pipe(
				filter((e): e is NavigationEnd => e instanceof NavigationEnd),
				takeUntilDestroyed(destroyRef),
			)
			.subscribe(() => {
				window.AnalyticsSDK?.track('page_view', {
					title: document.title,
				});
			});
	}

	private loadSdkOnce$(): Observable<void> {
		if (!this.sdkLoad$) {
			this.sdkLoad$ = defer(() => this.ensureSdkScript$()).pipe(shareReplay(1));
		}
		return this.sdkLoad$;
	}

	private ensureSdkScript$(): Observable<void> {
		if (typeof window === 'undefined') {
			return of(undefined);
		}
		if (window.AnalyticsSDK) {
			return of(undefined);
		}
		const src = environment.analyticsSdkScriptUrl?.trim();
		if (!src) {
			return of(undefined);
		}
		return new Observable<void>(subscriber => {
			const existing = document.querySelector('script[data-fenix-analytics-sdk]') as HTMLScriptElement | null;
			const done = () => {
				subscriber.next();
				subscriber.complete();
			};
			const fail = () => {
				subscriber.error(new Error('analytics.sdk.js failed to load'));
			};
			if (existing) {
				if (window.AnalyticsSDK) {
					done();
					return;
				}
				existing.addEventListener('load', done, { once: true });
				existing.addEventListener('error', fail, { once: true });
				return;
			}
			const script = document.createElement('script');
			script.src = src;
			script.async = true;
			script.dataset['fenixAnalyticsSdk'] = 'true';
			script.onload = () => done();
			script.onerror = () => fail();
			document.body.appendChild(script);
		});
	}
}
